import { Link, useForm } from '@inertiajs/react';
import AppLayout from '@/Layouts/AppLayout';
import PageHeader from '@/Components/PageHeader';
import FormInput from '@/Components/Form/FormInput';
import FormTextarea from '@/Components/Form/FormTextarea';
import FormSelect from '@/Components/Form/FormSelect';
import FormSection from '@/Components/Form/FormSection';

export default function ClientsCreate({ countries, users, sources }) {
    const { data, setData, post, processing, errors } = useForm({
        name: '',
        email: '',
        phone: '',
        phone_alt: '',
        company: '',
        designation: '',
        country_id: '',
        state: '',
        city: '',
        zip: '',
        address: '',
        gst_no: '',
        pan_no: '',
        dob: '',
        anniversary: '',
        source: '',
        assigned_to: '',
        notes: '',
        is_active: true,
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        post('/clients');
    };

    const sourceOptions = Object.entries(sources).map(([key, label]) => ({ id: key, name: label }));

    return (
        <AppLayout title="Add Client">
            <PageHeader
                title="Add Client"
                subtitle="Create a new client record"
                breadcrumbs={[
                    { label: 'CRM' },
                    { label: 'Clients', href: '/clients' },
                    { label: 'Create' },
                ]}
            />

            <form onSubmit={handleSubmit}>
                <div className="row">
                    <div className="col-lg-8">
                        <FormSection title="Personal Information">
                            <FormInput
                                className="col-md-6"
                                label="Full Name"
                                name="name"
                                value={data.name}
                                onChange={(v) => setData('name', v)}
                                error={errors.name}
                                required
                                placeholder="Enter full name"
                            />
                            <FormInput
                                className="col-md-6"
                                label="Company"
                                name="company"
                                value={data.company}
                                onChange={(v) => setData('company', v)}
                                placeholder="Company name"
                            />
                            <FormInput
                                className="col-md-6"
                                label="Email"
                                name="email"
                                type="email"
                                value={data.email}
                                onChange={(v) => setData('email', v)}
                                error={errors.email}
                                placeholder="email@example.com"
                            />
                            <FormInput
                                className="col-md-3"
                                label="Phone"
                                name="phone"
                                value={data.phone}
                                onChange={(v) => setData('phone', v)}
                                placeholder="+91 9876543210"
                            />
                            <FormInput
                                className="col-md-3"
                                label="Alt. Phone"
                                name="phone_alt"
                                value={data.phone_alt}
                                onChange={(v) => setData('phone_alt', v)}
                                placeholder="Alternative"
                            />
                            <FormInput
                                className="col-md-6"
                                label="Designation"
                                name="designation"
                                value={data.designation}
                                onChange={(v) => setData('designation', v)}
                                placeholder="e.g., Manager"
                            />
                            <FormInput
                                className="col-md-3"
                                label="Date of Birth"
                                name="dob"
                                type="date"
                                value={data.dob}
                                onChange={(v) => setData('dob', v)}
                            />
                            <FormInput
                                className="col-md-3"
                                label="Anniversary"
                                name="anniversary"
                                type="date"
                                value={data.anniversary}
                                onChange={(v) => setData('anniversary', v)}
                            />
                        </FormSection>

                        <FormSection title="Address">
                            <FormSelect
                                className="col-md-6"
                                label="Country"
                                name="country_id"
                                value={data.country_id}
                                onChange={(v) => setData('country_id', v)}
                                options={countries}
                                searchable
                                placeholder="Select Country"
                            />
                            <FormInput
                                className="col-md-6"
                                label="State"
                                name="state"
                                value={data.state}
                                onChange={(v) => setData('state', v)}
                                placeholder="State/Province"
                            />
                            <FormInput
                                className="col-md-6"
                                label="City"
                                name="city"
                                value={data.city}
                                onChange={(v) => setData('city', v)}
                                placeholder="City"
                            />
                            <FormInput
                                className="col-md-6"
                                label="ZIP/Postal Code"
                                name="zip"
                                value={data.zip}
                                onChange={(v) => setData('zip', v)}
                                placeholder="ZIP Code"
                            />
                            <FormTextarea
                                className="col-12"
                                label="Address"
                                name="address"
                                value={data.address}
                                onChange={(v) => setData('address', v)}
                                rows={2}
                                placeholder="Full address"
                            />
                        </FormSection>

                        <div className="d-flex gap-2">
                            <button type="submit" className="btn btn-primary" disabled={processing}>
                                {processing ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                        Creating...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-check-lg me-1"></i>
                                        Create Client
                                    </>
                                )}
                            </button>
                            <Link href="/clients" className="btn btn-outline-secondary">Cancel</Link>
                        </div>
                    </div>

                    <div className="col-lg-4">
                        <FormSection title="Business Details">
                            <FormInput
                                className="col-12"
                                label="GST Number"
                                name="gst_no"
                                value={data.gst_no}
                                onChange={(v) => setData('gst_no', v.toUpperCase())}
                                placeholder="e.g., 29ABCDE1234F1Z5"
                            />
                            <FormInput
                                className="col-12"
                                label="PAN Number"
                                name="pan_no"
                                value={data.pan_no}
                                onChange={(v) => setData('pan_no', v.toUpperCase())}
                                placeholder="e.g., ABCDE1234F"
                            />
                        </FormSection>

                        <FormSection title="Assignment">
                            <FormSelect
                                className="col-12"
                                label="Lead Source"
                                name="source"
                                value={data.source}
                                onChange={(v) => setData('source', v)}
                                options={sourceOptions}
                                searchable
                                placeholder="Select Source"
                            />
                            <FormSelect
                                className="col-12"
                                label="Assigned To"
                                name="assigned_to"
                                value={data.assigned_to}
                                onChange={(v) => setData('assigned_to', v)}
                                options={users}
                                searchable
                                placeholder="Select User"
                            />
                        </FormSection>

                        <div className="card">
                            <div className="card-header">
                                <h5 className="card-title mb-0">Status & Notes</h5>
                            </div>
                            <div className="card-body">
                                <div className="mb-3">
                                    <div className="form-check form-switch">
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            id="is_active"
                                            checked={data.is_active}
                                            onChange={(e) => setData('is_active', e.target.checked)}
                                        />
                                        <label className="form-check-label" htmlFor="is_active">Active</label>
                                    </div>
                                </div>
                                <FormTextarea
                                    label="Notes"
                                    name="notes"
                                    value={data.notes}
                                    onChange={(v) => setData('notes', v)}
                                    rows={4}
                                    placeholder="Internal notes about this client..."
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </AppLayout>
    );
}
